// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
// Hand-rolled SVG chart helpers. Pure layout functions (bins, ticks, scale,
// sparkline path) are exported for unit tests; the render helpers build SVG
// strings with [data-tip] hooks for the shared tooltip.

import { escapeHtml } from './format';

export interface Bin {
  x0: number;
  x1: number;
  count: number;
  items: number[];
}

/** Fixed-width histogram binning over [min,max]. Values on the right edge fall in the last bin. */
export function histogramBins(values: number[], binCount: number, min?: number, max?: number): Bin[] {
  if (binCount < 1) binCount = 1;
  const vs = values.filter((v) => Number.isFinite(v));
  const lo = min ?? (vs.length ? Math.min(...vs) : 0);
  const hi = max ?? (vs.length ? Math.max(...vs) : 1);
  const span = hi - lo || 1;
  const width = span / binCount;
  const bins: Bin[] = [];
  for (let i = 0; i < binCount; i++) bins.push({ x0: lo + i * width, x1: lo + (i + 1) * width, count: 0, items: [] });
  for (const v of vs) {
    let idx = Math.floor((v - lo) / width);
    if (idx < 0) idx = 0;
    if (idx >= binCount) idx = binCount - 1;
    bins[idx].count++;
    bins[idx].items.push(v);
  }
  return bins;
}

/** A "nice" upper bound for an axis (1/2/2.5/5 × 10^n ≥ value). */
export function niceMax(value: number): number {
  if (value <= 0) return 1;
  const exp = Math.floor(Math.log10(value));
  const base = Math.pow(10, exp);
  const f = value / base;
  let nf: number;
  if (f <= 1) nf = 1;
  else if (f <= 2) nf = 2;
  else if (f <= 2.5) nf = 2.5;
  else if (f <= 5) nf = 5;
  else nf = 10;
  return nf * base;
}

/** Evenly-spaced tick values from 0..max inclusive. */
export function axisTicks(max: number, count = 4): number[] {
  const ticks: number[] = [];
  for (let i = 0; i <= count; i++) ticks.push((max / count) * i);
  return ticks;
}

/** Points for a sparkline path scaled into [0,w]×[0,h] (y inverted). */
export function sparklinePoints(values: number[], w: number, h: number): [number, number][] {
  if (values.length === 0) return [];
  const max = Math.max(...values, 1);
  const n = values.length;
  return values.map((v, i) => {
    const x = n === 1 ? w / 2 : (i / (n - 1)) * w;
    const y = h - (v / max) * h;
    return [x, y];
  });
}

export function sparklineSvg(values: number[], w = 96, h = 26, colour = '#5a6b7b'): string {
  const pts = sparklinePoints(values, w, h - 4).map(([x, y]) => `${x.toFixed(1)},${(y + 2).toFixed(1)}`);
  if (pts.length === 0) return '';
  const line =
    pts.length === 1
      ? `<circle cx="${w / 2}" cy="${h / 2}" r="2" fill="${colour}"/>`
      : `<polyline points="${pts.join(' ')}" fill="none" stroke="${colour}" stroke-width="1.5" stroke-linejoin="round" stroke-linecap="round"/>`;
  return `<svg class="sparkline" viewBox="0 0 ${w} ${h}" width="${w}" height="${h}" preserveAspectRatio="none" aria-hidden="true">${line}</svg>`;
}

/** A horizontal segmented bar (category breakdown) as inline flex spans. */
export interface Segment {
  label: string;
  value: number;
  colour: string;
  tip?: string;
}
export function segmentedBar(segments: Segment[]): string {
  const total = segments.reduce((s, x) => s + Math.max(0, x.value), 0) || 1;
  const parts = segments
    .filter((s) => s.value > 0)
    .map((s) => {
      const pct = (s.value / total) * 100;
      const tip = escapeHtml(s.tip ?? `${s.label}: ${Math.round(s.value).toLocaleString()}`);
      return `<span class="seg" style="width:${pct.toFixed(2)}%;background:${s.colour}" data-tip="${tip}" aria-label="${tip}"></span>`;
    })
    .join('');
  return `<span class="segbar">${parts}</span>`;
}
